/**
 * Per-execution detail table for the Workflow context tab.
 *
 * Anchors on `automation_execution_logs` (one row per execution), enriched
 * with doc/automation/workflow names, actor (executor + owner) resolved to
 * display name + avatar, trigger source pill, duration and credits.
 */
import { CachedUserAvatar } from '@affine/core/components/cached-user-avatar';
import { useI18n } from '@affine/i18n';
import type { CSSProperties } from 'react';
import { useCallback, useEffect, useState } from 'react';

import {
  fetchWorkflowExecutionsDetail,
  type WorkflowExecutionDetail,
  type WorkflowExecutionsPage,
  type WorkflowExecutionUser,
  type WorkflowTriggerSource,
} from '../api';
import { formatCredits } from '../format';
import { Pagination } from '../pagination';
import { errorStyle, loadingStyle } from '../styles';
import type { AnalyticsPeriodRange } from '../types';
import { useAsyncResource } from '../use-async-resource';

const I18N =
  'com.streamlain.settings.workspace.automations.executions' as const;

const PAGE_SIZE = 20;

export interface WorkflowExecutionsTableProps {
  workspaceId: string;
  period: string | AnalyticsPeriodRange;
  pageSize?: number;
}

export const WorkflowExecutionsTable = ({
  workspaceId,
  period,
  pageSize = PAGE_SIZE,
}: WorkflowExecutionsTableProps) => {
  const t = useI18n();
  const [page, setPage] = useState(0);
  const periodCacheKey =
    typeof period === 'string'
      ? period
      : `${period.key}:${period.since}:${period.until}`;

  // Reset to first page when the month or the scoped workspace changes so
  // the user doesn't land on an empty page after switching selectors.
  useEffect(() => {
    setPage(0);
  }, [workspaceId, periodCacheKey]);

  const fetcher = useCallback(
    () =>
      fetchWorkflowExecutionsDetail(
        workspaceId,
        period,
        pageSize,
        page * pageSize
      ),
    [workspaceId, period, pageSize, page]
  );

  const { data, isLoading, error } = useAsyncResource<WorkflowExecutionsPage>(
    `workflow-executions:${workspaceId}:${periodCacheKey}:${pageSize}:${page}`,
    fetcher
  );

  if (isLoading && !data) {
    return <div style={loadingStyle}>Loading…</div>;
  }
  if (error) {
    return <div style={errorStyle}>{error}</div>;
  }
  const executions = data?.executions ?? [];
  const totalCount = data?.totalCount ?? 0;
  if (totalCount === 0) {
    return (
      <div style={emptyStyle}>
        {t[`${I18N}.empty`]?.() ?? 'No executions in this period'}
      </div>
    );
  }

  return (
    <div style={tableWrap}>
      <div style={scrollWrap}>
        <table style={table}>
          <thead>
            <tr>
              <th style={thWhen}>{t[`${I18N}.col.when`]?.() ?? 'When'}</th>
              <th style={thDoc}>{t[`${I18N}.col.doc`]?.() ?? 'Document'}</th>
              <th style={thName}>
                {t[`${I18N}.col.automation`]?.() ?? 'Automation'}
              </th>
              <th style={thName}>
                {t[`${I18N}.col.workflow`]?.() ?? 'Workflow'}
              </th>
              <th style={thTrigger}>
                {t[`${I18N}.col.trigger`]?.() ?? 'Trigger'}
              </th>
              <th style={thActor}>{t[`${I18N}.col.actor`]?.() ?? 'Actor'}</th>
              <th style={thDuration}>
                {t[`${I18N}.col.duration`]?.() ?? 'Duration'}
              </th>
              <th style={thCredits}>
                {t[`${I18N}.col.credits`]?.() ?? 'Credits'}
              </th>
            </tr>
          </thead>
          <tbody>
            {executions.map(row => (
              <ExecutionRow key={row.id} row={row} t={t} />
            ))}
          </tbody>
        </table>
      </div>
      <Pagination
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={setPage}
      />
      <div style={footnote}>
        {t[`${I18N}.footnote`]?.() ??
          'Billing: 0.5 credits per started 30 seconds.'}
      </div>
    </div>
  );
};

// ─── Row ─────────────────────────────────────────────────────────────────────

interface ExecutionRowProps {
  row: WorkflowExecutionDetail;
  t: ReturnType<typeof useI18n>;
}

const ExecutionRow = ({ row, t }: ExecutionRowProps) => {
  const when = formatWhen(row.createdAt);
  const triggerLabel = triggerSourceLabel(row.triggerSource, t);
  const duration = formatDuration(row.durationMs);
  return (
    <tr style={tr}>
      <td style={tdWhen} title={new Date(row.createdAt).toLocaleString()}>
        {when}
      </td>
      <td style={tdDoc} title={row.docName ?? ''}>
        {renderTruncated(row.docName)}
      </td>
      <td style={tdName} title={row.automationName ?? ''}>
        {renderTruncated(row.automationName)}
      </td>
      <td style={tdName} title={row.workflowName ?? ''}>
        {renderTruncated(row.workflowName)}
      </td>
      <td style={tdTrigger}>
        <span style={pill(row.triggerSource)}>{triggerLabel}</span>
      </td>
      <td style={tdActor}>
        <ActorCell row={row} t={t} />
      </td>
      <td style={tdDuration}>{duration}</td>
      <td style={tdCredits}>{formatCredits(row.credits)}</td>
    </tr>
  );
};

// ─── Actor cell ─────────────────────────────────────────────────────────────

interface ActorCellProps {
  row: WorkflowExecutionDetail;
  t: ReturnType<typeof useI18n>;
}

const ActorCell = ({ row, t }: ActorCellProps) => {
  const user = row.executor ?? row.owner;
  if (!user) return <span style={dimText}>—</span>;

  const isOwnerFallback = !row.executor && !!row.owner;
  const label = userLabel(user);
  const subtext = isOwnerFallback
    ? (t[`${I18N}.actor.owner-suffix`]?.() ?? '(owner)')
    : undefined;

  return (
    <div style={actorWrap}>
      <ActorAvatar user={user} />
      <div style={actorText}>
        <div style={actorName} title={user.email ?? user.id}>
          {label}
        </div>
        {subtext && <div style={actorSubtext}>{subtext}</div>}
      </div>
    </div>
  );
};

const ActorAvatar = ({ user }: { user: WorkflowExecutionUser }) => {
  return (
    <CachedUserAvatar
      userId={user.id}
      url={user.avatarUrl}
      name={userLabel(user)}
      size={20}
      style={avatarStyle}
    />
  );
};

function userLabel(user: WorkflowExecutionUser): string {
  if (user.name && user.name.trim().length > 0) return user.name;
  if (user.email) return user.email;
  return user.id.slice(0, 8);
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function renderTruncated(text: string | null) {
  if (!text || text.trim().length === 0) return <span style={dimText}>—</span>;
  return <span style={ellipsis}>{text}</span>;
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const sameDay =
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate();
  const time = d.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  });
  if (sameDay) return time;
  const date = d.toLocaleDateString(undefined, {
    day: '2-digit',
    month: '2-digit',
  });
  return `${date} ${time}`;
}

function triggerSourceLabel(
  src: WorkflowTriggerSource,
  t: ReturnType<typeof useI18n>
): string {
  const fallback: Record<WorkflowTriggerSource, string> = {
    manual: 'Manual',
    webhook: 'Webhook',
    cron: 'Schedule',
    test: 'Test',
    trigger: 'Trigger',
    unknown: '—',
  };
  return t[`${I18N}.trigger.${src}`]?.() ?? fallback[src];
}

function formatDuration(ms: number | null): string {
  if (ms === null || ms <= 0) return '—';
  if (ms < 1000) return `${ms} ms`;
  const sec = ms / 1000;
  if (sec < 60) return `${sec.toFixed(1)} s`;
  const min = Math.floor(sec / 60);
  const rem = Math.round(sec - min * 60);
  return `${min}m ${rem}s`;
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const tableWrap: CSSProperties = {
  width: '100%',
  marginTop: 12,
};

const scrollWrap: CSSProperties = {
  width: '100%',
  overflowX: 'auto',
};

const table: CSSProperties = {
  width: '100%',
  minWidth: 960,
  borderCollapse: 'collapse',
  fontSize: 12,
};

const thBase: CSSProperties = {
  textAlign: 'left',
  padding: '8px 10px',
  borderBottom: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
  color: 'var(--streamlain-v2-text-secondary)',
  fontWeight: 500,
  whiteSpace: 'nowrap',
};

const tdBase: CSSProperties = {
  padding: '8px 10px',
  verticalAlign: 'middle',
};

// Per-column widths — horizontal scroll kicks in when viewport < table minWidth.
const thWhen: CSSProperties = { ...thBase, minWidth: 130 };
const tdWhen: CSSProperties = {
  ...tdBase,
  minWidth: 130,
  whiteSpace: 'nowrap',
};

const thDoc: CSSProperties = { ...thBase, minWidth: 160 };
const tdDoc: CSSProperties = { ...tdBase, minWidth: 160, maxWidth: 240 };

const thName: CSSProperties = { ...thBase, minWidth: 140 };
const tdName: CSSProperties = { ...tdBase, minWidth: 140, maxWidth: 220 };

const thTrigger: CSSProperties = { ...thBase, minWidth: 96 };
const tdTrigger: CSSProperties = { ...tdBase, minWidth: 96 };

const thActor: CSSProperties = { ...thBase, minWidth: 180 };
const tdActor: CSSProperties = { ...tdBase, minWidth: 180, maxWidth: 260 };

const thDuration: CSSProperties = {
  ...thBase,
  textAlign: 'right',
  minWidth: 80,
};
const tdDuration: CSSProperties = {
  ...tdBase,
  textAlign: 'right',
  whiteSpace: 'nowrap',
  fontVariantNumeric: 'tabular-nums',
  minWidth: 80,
};

const thCredits: CSSProperties = {
  ...thBase,
  textAlign: 'right',
  minWidth: 80,
};
const tdCredits: CSSProperties = {
  ...tdBase,
  textAlign: 'right',
  whiteSpace: 'nowrap',
  fontVariantNumeric: 'tabular-nums',
  minWidth: 80,
  cursor: 'help',
};

const tr: CSSProperties = {
  borderBottom: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
};

const ellipsis: CSSProperties = {
  display: 'block',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const dimText: CSSProperties = {
  color: 'var(--streamlain-v2-text-secondary)',
};

const actorWrap: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  minWidth: 0,
};

const actorText: CSSProperties = {
  minWidth: 0,
  lineHeight: 1.3,
};

const actorName: CSSProperties = {
  ...ellipsis,
  fontWeight: 500,
};

const actorSubtext: CSSProperties = {
  fontSize: 10,
  color: 'var(--streamlain-v2-text-secondary)',
};

const avatarStyle: CSSProperties = {
  flexShrink: 0,
};

const emptyStyle: CSSProperties = {
  padding: '16px',
  color: 'var(--streamlain-v2-text-secondary)',
  fontSize: 13,
};

const footnote: CSSProperties = {
  marginTop: 8,
  fontSize: 11,
  color: 'var(--streamlain-v2-text-secondary)',
};

function pill(src: WorkflowTriggerSource): CSSProperties {
  const palette: Record<WorkflowTriggerSource, { bg: string; fg: string }> = {
    manual: { bg: 'rgba(59,130,246,0.12)', fg: 'rgb(37,99,235)' },
    webhook: { bg: 'rgba(16,185,129,0.12)', fg: 'rgb(5,150,105)' },
    cron: { bg: 'rgba(139,92,246,0.12)', fg: 'rgb(124,58,237)' },
    test: { bg: 'rgba(234,179,8,0.15)', fg: 'rgb(161,98,7)' },
    trigger: { bg: 'rgba(100,116,139,0.15)', fg: 'rgb(71,85,105)' },
    unknown: { bg: 'rgba(100,116,139,0.15)', fg: 'rgb(71,85,105)' },
  };
  const c = palette[src];
  return {
    display: 'inline-block',
    padding: '1px 8px',
    borderRadius: 10,
    fontSize: 10,
    fontWeight: 600,
    background: c.bg,
    color: c.fg,
    whiteSpace: 'nowrap',
  };
}
